import React, { useState } from "react";
import { Store, Plus, CheckCircle2, AlertTriangle, ShieldCheck, MapPin, Eye } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatDate } from "../../utils/formatters";
import { GRNModal } from "./GRNModal";
import { useSearchParams } from "react-router-dom";

export function ReceivingModule() {
  const { grns } = useApp();
  const [searchParams] = useSearchParams();
  const poParam = searchParams.get("po");

  const [isModalOpen, setIsModalOpen] = useState(Boolean(poParam));
  const [selectedGRN, setSelectedGRN] = useState(null);

  const columns = [
    {
      header: "GRN Number",
      accessorKey: "grnNumber",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white font-mono">{val}</span>
          <p className="text-[10px] text-slate-400">Date: {formatDate(row.receivedDate)}</p>
        </div>
      )
    },
    {
      header: "PO Reference",
      accessorKey: "poNumber",
      render: (val) => <span className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold">{val}</span>
    },
    {
      header: "Supplier",
      accessorKey: "supplierName",
      render: (val) => <span className="font-medium text-slate-800 dark:text-slate-200">{val}</span>
    },
    {
      header: "Warehouse",
      accessorKey: "warehouseName",
      render: (val) => <span className="text-slate-600 dark:text-slate-400 text-[11px]">{val?.split("(")[0]}</span>
    },
    {
      header: "Inspector",
      accessorKey: "inspector",
      render: (val) => <span className="text-xs text-slate-700 dark:text-slate-300">{val}</span>
    },
    {
      header: "Items Verified",
      accessorKey: "items",
      render: (items) => `${items?.length || 0} product batches`
    },
    {
      header: "Verification Status",
      accessorKey: "status",
      render: () => <Badge variant="emerald">Verified & Stored</Badge>
    },
    {
      header: "Actions",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedGRN(row);
          }}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Inspect Putaway Note"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Store className="w-5 h-5 text-emerald-500" />
            Goods Receipt Note (GRN) & Inward Receiving
          </h2>
          <p className="text-xs text-slate-500">
            Physical stock inspection, shortage & damage logging, batch number tagging, and exact bin putaway
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
        >
          <Plus className="w-4 h-4" />
          Receive Against Purchase Order (GRN)
        </button>
      </div>

      <DataTable
        columns={columns}
        data={grns}
        title="GRN Verification & Putaway Log"
        subtitle="All historical goods receipts with assigned shelf and batch codes"
        filename="kirana_goods_receipt_notes"
        onRowClick={(row) => setSelectedGRN(row)}
      />

      {/* GRN Detail Modal */}
      {selectedGRN && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white font-mono">
                  {selectedGRN.grnNumber}
                </h3>
                <p className="text-xs text-slate-500">
                  Ref: {selectedGRN.poNumber} • {selectedGRN.supplierName}
                </p>
              </div>
              <button
                onClick={() => setSelectedGRN(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                Received Line Items & Bin Placement
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedGRN.items?.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                  >
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">{item.productName}</p>
                      <p className="text-[11px] text-slate-400 font-mono">
                        Batch: {item.batchNo} • Expiry: {formatDate(item.expiryDate)}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                        {item.receivedQty} units in Bin {item.destinationBin}
                      </span>
                      {item.damagedQty > 0 && (
                        <p className="text-[10px] text-rose-500 font-bold">
                          Damaged: {item.damagedQty} units
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setSelectedGRN(null)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <GRNModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        defaultPoId={poParam}
      />
    </div>
  );
}
